'use client'

import React from 'react'
import { ArrowRight } from 'lucide-react'
import { LinkButton } from '@/components/ui/button'
import { PREFERRED_SOURCE_URL } from '@/lib/preferred-source'
import { isAnalyticsConsentGranted } from '@/lib/consent-manager'

interface PreferredSourceCTAProps {
  /** Override the default heading. */
  heading?: string
  /** Override the default explanatory blurb. */
  description?: string
  /** Override the button label. */
  ctaText?: string
  /** Placement tag sent as the GA4 event label (e.g. a post slug). */
  location?: string
}

/**
 * In-article CTA inviting AI-literate readers to add Pixelmojo as a Google
 * Preferred Source. Frame it as deliberate signal injection — our audience
 * understands why telling Google to prioritize a source matters.
 *
 * Place after the conclusion of high-value posts on agentic design, AI product
 * architecture, MCP, and AI visibility. Fires a `preferred_source_click` GA4
 * event (Google exposes no native publisher metrics, so the click is our proxy).
 *
 * Uses the shared marketing <LinkButton> (brand-pink, white label, pill) — no
 * hardcoded colors, so the label stays readable on the branded button.
 */
export default function PreferredSourceCTA({
  heading = 'Make sure this thinking reaches you in Google AI',
  description = 'Preferred Sources lets you tell Google to surface Pixelmojo more prominently in Top Stories, Discover, AI Overviews, and AI Mode. One click and our analysis on agentic design and AI product architecture follows you across Search.',
  ctaText = 'Add Pixelmojo as a Preferred Source',
  location = 'blog',
}: PreferredSourceCTAProps) {
  const handleClick = () => {
    if (
      typeof window !== 'undefined' &&
      isAnalyticsConsentGranted() &&
      window.gtag
    ) {
      window.gtag('event', 'preferred_source_click', {
        event_category: 'engagement',
        event_label: location,
        value: 1,
      })
    }
  }

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <h3 className='text-lg font-semibold text-foreground mb-2'>
          {heading}
        </h3>
        <p className='text-sm text-muted-foreground mb-5'>{description}</p>
        <LinkButton
          href={PREFERRED_SOURCE_URL}
          variant='default'
          size='default'
          onClick={handleClick}
          rightIcon={<ArrowRight />}
          className='!text-white !no-underline'
        >
          {ctaText}
        </LinkButton>
      </div>
    </div>
  )
}
